import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Card,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";
import AppConfig from "appConfig";
import { useAuthUser } from "react-auth-kit";
import * as XLSX from "xlsx";

function CallCenterContactsPage() {
  const pageTitle = "Contact List";
  usePageTitle(pageTitle);

  const auth = useAuthUser();
  const accountInfo = auth();

  const navigate = useNavigate();
  const { ref } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const [refreshPage, setRefreshPage] = useState(false);

  const [callCenter, setCallCenter] = useState({});
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);

  const [uploadFile, setUploadFile] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [openUploadContactsDialog, setOpenUploadContactsDialog] = useState(false);

  const [search, setSearch] = useState("");

  const [openEdit, setOpenEdit] = useState(false);
  const [editContact, setEditContact] = useState({
    reference: "",
    name: "",
    phone_number: "",
    tag: null,
  });

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/voice/call_centers/${ref}/contacts.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setCallCenter(result.data.data.call_center || {});
            setContacts(result.data.data.contacts || []);
            setTags(result.data.data.tags);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  const filteredContacts = useMemo(() => {
    if (!search) return contacts;

    const q = search.toLowerCase();

    return contacts.filter(
      (row) =>
        row.name?.toLowerCase().includes(q) ||
        row.phone_number?.toLowerCase().includes(q) ||
        row.tag?.title?.toLowerCase().includes(q)
    );
  }, [contacts, search]);

  const openEditDialog = (row) => {
    setEditContact({
      reference: row.reference,
      name: row.name,
      phone_number: row.phone_number,
      tag: row.tag || null,
    });
    setOpenEdit(true);
  };

  const saveContact = async () => {
    if (!editContact.name || !editContact.phone_number) {
      setSeverity("error");
      setErrMsg("Name and phone number are required");
      setOpen(true);
      return;
    }

    setShowLoading(true);
    setOpenEdit(false);

    const json = {
      reference: editContact.reference,
      name: editContact.name,
      phone_number: editContact.phone_number,
      tag: editContact.tag?.reference || null,
    };

    await axios
      .post("/portal/voice/call_centers/contacts/edit.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setOpenEdit(false);
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!"
        );
      });
  };

  const deleteContact = async (reference) => {
    const confirmed = await statusDialog.confirm(
      "Delete this contact?",
      "This action cannot be undone."
    );

    if (!confirmed) return;

    setShowLoading(true);

    await axios
      .delete(`/portal/voice/call_centers/contacts/${reference}/delete.json`)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);
        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!"
        );
      });
  };

  const exportContacts = () => {
    window.open(
      `${AppConfig.api_base_url}portal/voice/call_centers/contacts/${ref}/${accountInfo.user.reference}/export.json`,
      "_blank"
    );
  };

  const columns = [
    { Header: "Name", accessor: "name" },
    { Header: "Phone Number", accessor: "phone_number" },
    {
      Header: "Tag",
      accessor: "tag",
      Cell: ({ value }) =>
        value ? (
          <Chip label={value.title} size="small" variant="outlined" />
        ) : (
          <MDTypography variant="caption" color="text">
            ---
          </MDTypography>
        ),
    },
    {
      Header: "Called",
      accessor: "called",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Called" : "Pending"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "right",
      disableSorting: true,
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDButton
            iconOnly
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => openEditDialog(row.original)}
          >
            <Icon>edit</Icon>
          </MDButton>
          <MDButton
            iconOnly
            size="small"
            variant="outlined"
            color="error"
            onClick={() => deleteContact(row.original.reference)}
          >
            <Icon>delete</Icon>
          </MDButton>
        </MDBox>
      ),
    },
  ];

  function downloadTemplate(headers, filename = "sample_upload.xlsx") {
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, filename);
  }

  async function fileSelected(e) {
    const fileTypes = ["xlsx", "xls", "csv"];
    const file = e.target.files[0];
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    const supported = fileTypes.includes(extension);
    if (!supported) {
      setSeverity("error");
      setErrMsg("Only excel and csv files accepted.");
      setOpen(true);
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      if (!json.length) {
        throw new Error("Empty file");
      }

      // First row = headers
      const headers = json[0].map((h) => String(h).toLowerCase().trim());
      const requiredColumns = ["name", "phone_number"];
      const missing = requiredColumns.filter((col) => !headers.includes(col));
      if (missing.length > 0) {
        setSeverity("error");
        setErrMsg(`Missing required columns: ${missing.join(", ")}`);
        setOpen(true);
        return;
      }

      setUploadFile(file);
    } catch (err) {
      setSeverity("error");
      setErrMsg("Invalid or unreadable file.");
      setOpen(true);
    }
  }

  async function uploadContacts() {
    if (!uploadFile) {
      return;
    }

    const formData = new FormData();
    formData.append("reference", ref);
    if (selectedTag) {
      formData.append("tag", selectedTag);
    }
    formData.append("file", uploadFile);

    // console.log(formData)
    setOpenUploadContactsDialog(false);
    setShowLoading(true);
    await axios
      .post("/portal/voice/call_centers/contacts/upload.json", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");
          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Action Failed!");
        }
      })
      .catch(() => {
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
        setShowLoading(false);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openUploadContactsDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Upload Contacts</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mt={1} mb={3}>
            <MDBox>
              <TextField
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                type="file"
                name="upload_file"
                id="upload_file"
                onChange={(e) => fileSelected(e)}
                fullWidth
              />
            </MDBox>
            <MDBox mt={1}>
              <Alert severity="warning">
                <MDTypography variant="subtitle2" fontSize={14} mb={1}>
                  Download sample upload template.
                </MDTypography>
                <MDButton
                  size="small"
                  variant="contained"
                  color="dark"
                  onClick={() => {
                    let headers = ["name", "phone_number"];
                    downloadTemplate(headers, "sample_entity_upload.xlsx");
                  }}
                >
                  Sample Template
                </MDButton>
              </Alert>
            </MDBox>

            <MDBox mt={4}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Contact Tag (Optional)</InputLabel>
                <Select
                  label="Contact Tag (Optional)"
                  onChange={(e) => {
                    setSelectedTag(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    tags.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      tags.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenUploadContactsDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              uploadContacts();
            }}
          >
            Upload
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} fullWidth maxWidth="sm">
        <DialogTitle>
          <MDBox>Edit Contact</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox pt={1} display="flex" flexDirection="column" gap={3}>
            <TextField
              label="Name"
              fullWidth
              value={editContact.name}
              onChange={(e) => setEditContact({ ...editContact, name: e.target.value })}
            />

            <TextField
              label="Phone Number"
              fullWidth
              value={editContact.phone_number}
              onChange={(e) =>
                setEditContact({
                  ...editContact,
                  phone_number: e.target.value,
                })
              }
            />
            <TextField
              select
              fullWidth
              label="Tag"
              value={editContact.tag?.reference || ""}
              onChange={(e) => {
                const tag = tags.find((t) => t.reference === e.target.value) || null;

                setEditContact({
                  ...editContact,
                  tag: tag,
                });
              }}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              <MenuItem value="">No tag</MenuItem>

              {tags.map((tag) => (
                <MenuItem key={tag.reference} value={tag.reference}>
                  {tag.title}
                </MenuItem>
              ))}
            </TextField>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveContact}>
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mt={1} mb={5}>
        <MDBox mb={2}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/voice/call-centers/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>
        <MDBox mb={1}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            px={1}
          >
            <MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon>contacts</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Contact List
                </MDTypography>
              </MDBox>

              <MDTypography variant="button" color="text" fontWeight="light">
                {callCenter.title}
              </MDTypography>
            </MDBox>

            <MDBox display="flex" gap={1}>
              <MDButton
                color="dark"
                variant="contained"
                size="small"
                iconOnly
                onClick={() => setOpenUploadContactsDialog(true)}
              >
                <Icon>upload</Icon>
              </MDButton>
              <MDButton
                color="dark"
                variant="outlined"
                size="small"
                iconOnly
                onClick={exportContacts}
              >
                <Icon>download</Icon>
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mt={2}>
            <Divider />
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <MDTypography mb={1} variant="h6">
                Filter Contacts
              </MDTypography>
              <TextField
                fullWidth
                placeholder="Search by name, phone number or tag"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
          </Card>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={2}>
            {filteredContacts.length === 0 && (
              <Card sx={{ marginTop: 0 }}>
                <MDBox p={4} textAlign="center">
                  <Icon sx={{ fontSize: 48, color: "text.secondary" }}>contacts</Icon>

                  <MDTypography variant="h6" fontWeight="medium" mt={1}>
                    No contacts found
                  </MDTypography>

                  <MDTypography variant="button" color="text" fontWeight="light">
                    Try adjusting your search.
                  </MDTypography>
                </MDBox>
              </Card>
            )}

            {filteredContacts.map((row) => (
              <Card key={row.reference} sx={{ marginTop: 0 }}>
                <MDBox p={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <MDBox flex={1}>
                      <MDTypography variant="h6" fontWeight="medium">
                        {row.name}
                      </MDTypography>

                      <MDTypography variant="button" color="text" fontWeight="light">
                        {row.phone_number}
                      </MDTypography>
                    </MDBox>

                    <Chip
                      label={row.called ? "Called" : "Pending"}
                      color={row.called ? "success" : "default"}
                      size="small"
                    />
                  </MDBox>

                  <MDBox mt={2}>
                    <Chip label={row.tag} size="small" variant="outlined" />
                  </MDBox>

                  <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <MDButton
                      size="small"
                      color="dark"
                      variant="text"
                      startIcon={<Icon>edit</Icon>}
                      onClick={() => openEditDialog(row)}
                    >
                      Edit
                    </MDButton>

                    <MDButton
                      size="small"
                      color="error"
                      variant="text"
                      startIcon={<Icon>delete</Icon>}
                      onClick={() => deleteContact(row.reference)}
                    >
                      Delete
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            ))}
          </MDBox>
        ) : (
          <Card sx={{ marginTop: 0 }}>
            <MDBox p={0}>
              <DataTable
                table={{
                  columns,
                  rows: filteredContacts,
                }}
                isSorted={true}
                entriesPerPage={{
                  defaultValue: 10,
                  entries: [50, 100, 500, 1000],
                }}
                showTotalEntries={true}
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </Card>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default CallCenterContactsPage;
